/**
 * Badge rendering utilities for DataTable cell renderers
 *
 * This module provides reusable badge components for common data types
 * like statuses, types, and other categorized values.
 */

import React from 'react';
import { Badge } from 'react-bootstrap';
import {
  getActivityTypeColors,
  getDeviceColors,
  getHostColors,
  getCustomerTypeColors,
  getFormatBadgeVariant,
  isKnownActivityType,
} from './colorUtils';
import { splitPipeDelimited } from './formatters';

/**
 * Render a Yes/No/Unknown status badge
 */
export const renderStatusBadge = (value: string | undefined | null): React.ReactNode => {
  if (!value || value === '') {
    return null;
  }

  const normalized = value.trim();

  if (normalized === 'Yes') {
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="success">
          <i className="bi bi-check-lg"></i> Yes
        </Badge>
      </div>
    );
  } else if (normalized === 'No') {
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="danger">
          <i className="bi bi-x-lg"></i> No
        </Badge>
      </div>
    );
  } else if (normalized === 'Unknown') {
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="secondary">
          <i className="bi bi-question-lg"></i> Unknown
        </Badge>
      </div>
    );
  } else {
    // Fallback: display the actual value in a secondary badge
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="secondary">{normalized}</Badge>
      </div>
    );
  }
};

/**
 * Render an invoiced status badge with specific values
 */
export const renderInvoicedBadge = (value: string | undefined | null): React.ReactNode => {
  if (!value || value === '') {
    return null;
  }

  const normalized = value.trim();

  if (normalized === 'Sent') {
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="warning">
          <i className="bi bi-send"></i> Sent
        </Badge>
      </div>
    );
  } else if (normalized === 'Received') {
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="success">
          <i className="bi bi-check-circle"></i> Received
        </Badge>
      </div>
    );
  } else if (normalized === 'Need To Send') {
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="danger">
          <i className="bi bi-exclamation-circle"></i> Need To Send
        </Badge>
      </div>
    );
  } else if (normalized === 'Unknown') {
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="secondary">
          <i className="bi bi-question-lg"></i> Unknown
        </Badge>
      </div>
    );
  } else {
    // Fallback: display the actual value in a secondary badge
    return (
      <div className="d-flex justify-content-center">
        <Badge bg="secondary">{normalized}</Badge>
      </div>
    );
  }
};

/**
 * Render an activity type badge with appropriate colors
 */
export const renderActivityTypeBadge = (activityType: string | undefined | null): React.ReactNode => {
  if (!activityType) return null;

  const colors = getActivityTypeColors(activityType);
  const isKnown = isKnownActivityType(activityType);

  return (
    <span
      className="badge"
      style={{
        backgroundColor: colors.background,
        color: colors.text,
        display: 'inline-block',
        minWidth: isKnown ? '105px' : 'auto',
        width: isKnown ? '105px' : 'auto',
        textAlign: 'left',
      }}
    >
      {activityType}
    </span>
  );
};

/**
 * Render device badges (can be multiple, pipe-delimited)
 */
export const renderDeviceBadges = (devices: string | undefined | null): React.ReactNode => {
  if (!devices) return null;

  const deviceList = splitPipeDelimited(devices);

  return (
    <div className="d-flex align-items-center gap-1 flex-wrap">
      {deviceList.map((device, index) => {
        const colors = getDeviceColors(device);
        return (
          <span
            key={index}
            className="badge"
            style={{
              backgroundColor: colors.background,
              color: colors.text,
              textAlign: 'left',
              display: 'inline-block',
              width: 'fit-content',
              whiteSpace: 'normal',
              lineHeight: '1',
            }}
          >
            {device}
          </span>
        );
      })}
    </div>
  );
};

/**
 * Render a format badge (Virtual, Hybrid, In-Person)
 */
export const renderFormatBadge = (format: string | undefined | null): React.ReactNode => {
  if (!format) return null;

  const variant = getFormatBadgeVariant(format);
  return <Badge bg={variant}>{format}</Badge>;
};

/**
 * Render a customer type badge
 */
export const renderCustomerTypeBadge = (customerType: string | undefined | null): React.ReactNode => {
  const colors = getCustomerTypeColors(customerType);
  return (
    <span
      className="badge"
      style={{
        backgroundColor: colors.background,
        color: colors.text,
        textAlign: 'left',
        display: 'inline-block',
        width: 'fit-content',
      }}
    >
      {customerType || 'Unknown'}
    </span>
  );
};

/**
 * Render a TBD (To Be Determined) badge
 */
export const renderTBDBadge = (): React.ReactNode => {
  return (
    <div className="d-flex justify-content-center align-items-center">
      <Badge bg="warning">TBD</Badge>
    </div>
  );
};

/**
 * Render a host badge with appropriate colors
 * @param host - Host value
 * @param url - Optional URL to make the badge a link
 */
export const renderHostBadge = (
  host: string | undefined | null,
  url?: string | null,
  options?: { mismatch?: boolean }
): React.ReactNode => {
  if (!host) return null;

  const colors = getHostColors(host);

  // A shift that can't be covering its event takes the app's error red instead
  // of the host's identity colour. When one person works two shifts on the same
  // event, both badges carry the same name, so colour is the only thing that
  // says which of them is wrong.
  const mismatch = !!options?.mismatch;

  const badgeContent = (
    <span
      className="badge"
      style={{
        backgroundColor: mismatch ? '#dc3545' : colors.background,
        color: mismatch ? '#fff' : colors.text,
        textAlign: 'left',
        whiteSpace: 'normal',
        display: 'inline-block',
        width: 'fit-content',
        fontSize: '0.875rem',
      }}
    >
      {mismatch && <i className="bi bi-exclamation-triangle-fill me-1"></i>}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          {host}
        </a>
      ) : (
        host
      )}
    </span>
  );

  return badgeContent;
};
